// src/pages/register-options/RegisterTableContadoOthers.jsx
import React, {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from "react";
import axios from "axios";
import Select from "react-select";
import {
    handleTextInput,
    handleImeiInput,
    handleNumberInput,
    handleCustomInput,
    handlePriceInput,
} from "../../utils/inputUtils";
import PreloadFetch from "../PreloadFetch";
import AccessorySalePreviewModal from "../accessories/AccessorySalePreviewModal";

import moment from "moment-timezone";
import "moment/locale/es";
moment.locale("es");

/* ============ CONFIG API DNI ============ */
const DNI_API_URL = "https://apiperu.dev/api/dni";
const DNI_API_BEARER = process.env.REACT_APP_APIPERU_TOKEN;

/* ============ AXIOS aislado para DNI ============ */
const axiosDni = axios.create({
    baseURL: DNI_API_URL,
    headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${DNI_API_BEARER || ""}`,
    },
    withCredentials: false,
    validateStatus: () => true,
});

const RegisterTableContadoOthers = forwardRef((props, ref) => {
    const {
        options: phoneOptions,
        onSendDataCat,
        onSendDataSubCat,
        onSendDataSegment,
        sendSubCat,
    } = props;

    const [accessoryPreviewOpen, setAccessoryPreviewOpen] = useState(false);
    useEffect(() => {
        if (String(sendSubCat) === "3") setAccessoryPreviewOpen(true);
    }, [sendSubCat]);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPhone, setSelectedPhone] = useState(null);

    // toggle autocompletar y control de de-bounce
    const [autoOn, setAutoOn] = useState(true);
    const [lastKey, setLastKey] = useState("");

    const createDateSet = moment
        .tz("America/Lima")
        .format("dddd DD [de] MMMM [del] YYYY");
    const classNameColorID = sendSubCat === "3" ? "8" : "9";

    const catRef = useRef();
    const subCatRef = useRef();
    const segmentRef = useRef();
    const descriptionRef = useRef();
    const phoneRef = useRef();
    const phonePriceRef = useRef();
    const nameRef = useRef();
    const paternalSurRef = useRef();
    const maternalSurRef = useRef();
    const fmRef = useRef();
    const fmAmountRef = useRef();
    const imeiRef = useRef();
    const dniRef = useRef();
    const emailRef = useRef();
    const phoneNumberRef = useRef();
    const cashAmountRef = useRef();
    const onlinePayRef = useRef();
    const onlineAmountRef = useRef();
    const applicanRef = useRef();

    // abort/debounce
    const abortDniRef = useRef(null);
    const debounceRef = useRef(null);

    /* ==================== Imperative API ==================== */
    useImperativeHandle(ref, () => ({
        getValues: () => ({
            cat: catRef.current?.value,
            subCat: subCatRef.current?.value,
            segment: segmentRef.current?.value,
            description: descriptionRef.current?.value,
            phone: selectedPhone?.value,
            phonePrice: phonePriceRef.current?.value,
            name: nameRef.current?.value,
            paternalSur: paternalSurRef.current?.value,
            maternalSur: maternalSurRef.current?.value,
            fm: fmRef.current?.value,
            fmAmount: fmAmountRef.current?.value,
            imei: imeiRef.current?.value,
            dni: dniRef.current?.value,
            email: emailRef.current?.value,
            phoneNumber: phoneNumberRef.current?.value,
            cashAmount: cashAmountRef.current?.value,
            onlinePay: onlinePayRef.current?.value,
            onlineAmount: onlineAmountRef.current?.value,
            applicant: applicanRef.current?.value,
        }),
    }));

    const handleChange = (option) => {
        setSelectedPhone(option);
    };

    const changeCat = (e) => {
        onSendDataCat({
            cat: e.target.value,
        });
        if (String(e.target.value) === "2" && String(subCatRef.current?.value) === "3") setAccessoryPreviewOpen(true);
        else setAccessoryPreviewOpen(false);
        clearPending();
        clearNames({ keepDni: true });
    };

    const [subCat, setSubCat] = useState(3);
    const changeSubCat = (e) => {
        onSendDataSubCat({
            cat: catRef.current.value,
            subCat: e.target.value,
        });
        setSubCat(e.target.value);
        if (String(e.target.value) === "3" && String(catRef.current?.value) === "2") {
            setAccessoryPreviewOpen(true);
        } else {
            setAccessoryPreviewOpen(false);
        }
        clearPending();
        clearNames({ keepDni: true });

        const v = (dniRef.current?.value || "").trim();
        if (autoOn && /^\d{8}$/.test(v)) autoFillByDni(v, false);
    };

    const changeSegment = (e) => {
        onSendDataSegment({
            cat: catRef.current.value,
            subCat: subCatRef.current.value,
            segment: e.target.value,
        });
        clearPending();
        clearNames({ keepDni: true });

        const v = (dniRef.current?.value || "").trim();
        if (autoOn && /^\d{8}$/.test(v)) autoFillByDni(v, false);
    };

    /* ==================== Helpers ==================== */
    const clearPending = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        try {
            abortDniRef.current?.abort?.();
        } catch {}
    };

    const clearNames = ({ keepDni = true } = {}) => {
        if (!keepDni && dniRef.current) dniRef.current.value = "";
        if (nameRef.current) nameRef.current.value = "";
        if (paternalSurRef.current) paternalSurRef.current.value = "";
        if (maternalSurRef.current) maternalSurRef.current.value = "";
    };

    const toTitleCase = (s = "") =>
        s
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim()
            .replace(/\b([a-záéíóúñü])([a-záéíóúñü]*)/gi, (_, a, b) => a.toUpperCase() + b);

    const setNamesFromApi = (payload) => {
        const root = payload?.data ? payload.data : payload || {};
        const nombres = toTitleCase(root.nombres || root.nombre || "");
        const apPat = toTitleCase(root.apellido_paterno || root.apellidoPaterno || "");
        const apMat = toTitleCase(root.apellido_materno || root.apellidoMaterno || "");
        if (nameRef.current) nameRef.current.value = nombres;
        if (paternalSurRef.current) paternalSurRef.current.value = apPat;
        if (maternalSurRef.current) maternalSurRef.current.value = apMat;
    };

    /* ==================== Fetch DNI API ==================== */
    const fetchDniApi = async (dniVal) => {
        if (!/^\d{8}$/.test(dniVal)) return null;
        if (!DNI_API_BEARER) return null;
        try {
            try {
                abortDniRef.current?.abort?.();
            } catch {}
            abortDniRef.current = new AbortController();
            const res = await axiosDni.post(
                "",
                { dni: dniVal },
                { signal: abortDniRef.current.signal }
            );
            if (res.status === 200 && res.data) return res.data;
            return null;
        } catch (err) {
            console.error("[DNI API] Error:", err?.message || err);
            return null;
        }
    };

    /* ==================== Autocomplete core ==================== */
    // Para esta tabla: Solo cuando Categoría es Contado (2). Subcategoría puede ser 3/4/5.
    const autoFillByDni = (dniVal, useDebounce = true) => {
        if (!autoOn) return;
        if (catRef.current?.value !== "2") return;
        if (useDebounce) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => doAutoFill(dniVal), 1500);
        } else {
            doAutoFill(dniVal);
        }
    };

    const doAutoFill = async (dniVal) => {
        if (!autoOn) return;
        const key = `dni-${dniVal}-c${catRef.current?.value}-s${subCatRef.current?.value}`;
        if (lastKey === key) return;
        setLastKey(key);

        setLoading(true);
        try {
            const dniInfo = await fetchDniApi(dniVal);
            if (dniInfo) setNamesFromApi(dniInfo);
        } finally {
            setLoading(false);
        }
    };

    const onDniChange = (e) => {
        handleNumberInput(e, 8);
        const v = (e.target.value || "").trim();
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (v.length === 8 && autoOn) autoFillByDni(v, true);
    };

    /* ==================== Cleanup ==================== */
    useEffect(() => {
        return () => {
            try {
                abortDniRef.current?.abort?.();
            } catch {}
        };
    }, []);

    /* ==================== Render ==================== */
    return (
        <div className={`registerTable registerTable${classNameColorID} registerTableInList`}>
            {loading && <PreloadFetch className="mainPreloadOpacity" />}
            {accessoryPreviewOpen && <AccessorySalePreviewModal onClose={() => setAccessoryPreviewOpen(false)} />}
            <table>
                <caption>
                    <div>
                        <span>{createDateSet}</span>
                        <label className="toggleTwo" style={{ marginLeft: 12 }}>
                            <input
                                type="checkbox"
                                className="toggleTwo-input"
                                checked={autoOn}
                                onChange={(e) => {
                                    setAutoOn(e.target.checked);
                                    if (e.target.checked) {
                                        const v = (dniRef.current?.value || "").trim();
                                        if (/^\d{8}$/.test(v)) autoFillByDni(v, false);
                                    } else {
                                        clearPending();
                                    }
                                }}
                            />
                            <span className="toggleTwo-slider" />
                            <span className="toggleTwo-text">Autocompletar por DNI</span>
                        </label>
                    </div>
                </caption>

                <thead>
                    <tr>
                        <th>Tipo de Registro</th>
                        <th>Tipo de Producto</th>
                        <th>Fuente</th>
                        <th>Descripción</th>
                        <th>Nombre</th>
                        <th>Apellido 1</th>
                        <th>Apellido 2</th>
                        <th>DNI</th>
                        <th>EMAIL</th>
                        <th>Celular</th>
                        <th>Monto Efectivo</th>
                        <th>Pago Online</th>
                        <th>Monto Online</th>
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        <td className="cat">
                            <select defaultValue="2" onChange={changeCat} ref={catRef}>
                                <option value="1">Crédito</option>
                                <option value="2">Contado</option>
                                <option value="3">Egreso</option>
                                <option value="4">Ingreso</option>
                                <option value="5">Retiro</option>
                            </select>
                        </td>
                        <td className="subCat">
                            <select defaultValue={sendSubCat} onChange={changeSubCat} ref={subCatRef}>
                                <option value="1">Télefono</option>
                                <option value="2">Cuota</option>
                                <option value="3">Accesorios</option>
                                <option value="4">Formateo</option>
                                <option value="5">Chip</option>
                                <option value="6">FM</option>
                            </select>
                        </td>
                        <td className="segment">
                            <select disabled defaultValue="4" onChange={changeSegment} ref={segmentRef}>
                                <option value="4">MovilPro</option>
                            </select>
                        </td>
                        <td width="12%" className="tableWidth">
                            <input
                                type="text"
                                ref={descriptionRef}
                                onInput={(e) => handleCustomInput(e, 100)}
                                id="descriptionAdd"
                            />
                        </td>

                        {/* Nombre / Apellidos: deshabilitados cuando el toggle está ON */}
                        <td>
                            <input
                                type="text"
                                onInput={(e) => handleTextInput(e, 40)}
                                ref={nameRef}
                                id="nameAdd"
                                disabled={autoOn}
                                aria-disabled={autoOn}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                onInput={(e) => handleTextInput(e, 40)}
                                ref={paternalSurRef}
                                id="paternalSurAdd"
                                disabled={autoOn}
                                aria-disabled={autoOn}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                onInput={(e) => handleTextInput(e, 40)}
                                ref={maternalSurRef}
                                id="maternalSurAdd"
                                disabled={autoOn}
                                aria-disabled={autoOn}
                            />
                        </td>

                        <td>
                            <input
                                type="number"
                                onInput={(e) => handleNumberInput(e, 8)}
                                onChange={onDniChange}
                                ref={dniRef}
                                id="dniAdd"
                            />
                        </td>
                        <td width="10%">
                            <input type="text" ref={emailRef} id="emailAdd" />
                        </td>
                        <td>
                            <input
                                type="number"
                                onInput={(e) => handleNumberInput(e, 9)}
                                ref={phoneNumberRef}
                                id="phoneNumberAdd"
                            />
                        </td>
                        <td width="5%" className="withSymbol">
                            <span>S/.</span>
                            <input
                                type="text"
                                defaultValue={0.0}
                                onInput={handlePriceInput}
                                ref={cashAmountRef}
                            />
                        </td>
                        <td>
                            <select defaultValue="0" ref={onlinePayRef} id="onlinePayAdd">
                                <option value="0">Selecciónar</option>
                                <option value="1">Yape</option>
                                <option value="5">Culqi Yape</option>
                                <option value="2">Tarjeta</option>
                                <option value="6">Culqi Tarjeta</option>
                                <option value="3">Transferencia</option>
                            </select>
                        </td>
                        <td width="5%" className="withSymbol">
                            <span>S/.</span>
                            <input
                                type="text"
                                defaultValue={0.0}
                                onInput={handlePriceInput}
                                ref={onlineAmountRef}
                                id="onlineAmountAdd"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
});

export default RegisterTableContadoOthers;
